"use client";

/**
 * "Buy vs stream" — the /why-bb calculator as a /launch slide (Gordon
 * 2026-10-29: "Explore why BB section should not be a link but should be a
 * section on the launch page like the simulation on breakinbread.com/why-bb
 * what buying means in streaming compared to each streaming platform").
 *
 * One price slider; the maker's net from a direct sale on BB (π % + card fee)
 * set against how many streams / views / uses every platform needs to pay the
 * same, all at once. Rates live in `data/stream-rates.ts` (sourced, dated),
 * shared with /why-bb. Bars are log-scaled — TikTok needs ~1,000× Amazon.
 */

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { directSaleEstimate, royaltyUnitsToEqual } from "@/lib/royalty-calculator";
import { PLATFORM_FEE_RATE } from "@/lib/platform-fee";
import { RATE_SOURCES, STREAM_RATES, STREAM_RATES_CHECKED_ON } from "@/data/stream-rates";
import { CurrencySelect, niceStep, snap, useDisplayCurrency } from "@/components/currency/display-currency";
import { cn } from "@/lib/utils";
import { PlatformLogo, hasPlatformLogo } from "@/components/icons/platform-logos";

// Stripe's standard EEA card pricing (the fixed part is €0.25, converted).
const CARD_PERCENT = 1.5;
const CARD_FIXED_EUR = 0.25;

export function BuyVsStream() {
  const t = useTranslations("launch");
  const locale = useLocale();
  // Any currency at today's rate (Gordon 2026-11-01). The price is kept as
  // euros so switching currency keeps the same purchase; the slider runs from
  // 10 kr to 1 000 kr worth (Gordon 2026-11-02) in round steps of the chosen currency.
  const cur = useDisplayCurrency();
  const [priceEur, setPriceEur] = useState(10);
  // Tap a platform and the headline counts for it (Gordon 2026-11-02: "click on
  // different streaming services to change the left column data").
  const [picked, setPicked] = useState("spotify");
  const step = niceStep(1000 * cur.perSek, 100);
  const minLocal = Math.max(step, snap(10 * cur.perSek, step)), maxLocal = snap(1000 * cur.perSek, step);
  const priceLocal = Math.min(maxLocal, snap(priceEur * cur.perEur, step));
  const priceCents = Math.round(priceLocal * 100);

  const calc = directSaleEstimate(priceCents, PLATFORM_FEE_RATE, CARD_PERCENT, Math.round(CARD_FIXED_EUR * cur.perEur * 100));
  const money = (cents: number) => cur.money(cents / 100);
  const count = (n: number) => new Intl.NumberFormat(locale).format(n);

  // Fewest streams first; platforms without a published rate last. Rates are
  // USD per unit, converted to the chosen currency.
  const rows = STREAM_RATES
    .map(r => ({ ...r, needed: royaltyUnitsToEqual(calc.netCents, r.usd === null ? null : r.usd * cur.perUsd) }))
    .sort((a, b) => (a.needed ?? Infinity) - (b.needed ?? Infinity));
  const known = rows.map(r => r.needed).filter((n): n is number => n !== null && n > 0);
  const lo = Math.log10(Math.min(...known));
  const hi = Math.log10(Math.max(...known));
  // Bar length on a log scale, so Amazon and TikTok both read on one slide.
  const barWidth = (n: number) => 10 + 90 * ((Math.log10(n) - lo) / Math.max(hi - lo, 0.001));
  const pickedRow = rows.find(r => r.key === picked && r.needed !== null) ?? rows.find(r => r.key === "spotify")!;
  const unitWord = (unit: string) => t(unit === "stream" ? "buyUnitStreams" : unit === "view" ? "buyUnitViews" : "buyUnitUses");
  const checked = new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(new Date(`${STREAM_RATES_CHECKED_ON}T12:00:00`));
  const fxDate = cur.updated ? new Intl.DateTimeFormat(locale, { dateStyle: "long", timeStyle: "short" }).format(new Date(cur.updated)) : null;

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
      {/* As tall as the list beside it, with the room spread between its parts. */}
      <div className="flex flex-col justify-between gap-8">
        <p className="bb-reveal text-sm sm:text-base lg:text-xl font-semibold uppercase tracking-[0.25em] text-[var(--launch-accent-2)]">{t("buyEyebrow")}</p>
        <h2 className="bb-reveal bb-impact text-[clamp(1.9rem,3.6vw,3.9rem)]" style={{ ["--d" as string]: "120ms" }}>
          {pickedRow.key === "spotify"
            ? t("buyHeadline", { price: money(priceCents), count: count(pickedRow.needed ?? 0) })
            : t("buyHeadlinePlatform", { price: money(priceCents), count: count(pickedRow.needed ?? 0), unit: unitWord(pickedRow.unit), platform: pickedRow.label })}
        </h2>
        <div className="bb-reveal space-y-2" style={{ ["--d" as string]: "250ms" }}>
          <CurrencySelect value={cur.code} onChange={cur.setCode} options={cur.options} />
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="launch-buy-price" className="text-base sm:text-lg font-semibold">{t("buyPriceLabel")}</label>
            <span className="bb-impact text-3xl sm:text-4xl text-[var(--launch-accent-2)]">{money(priceCents)}</span>
          </div>
          <input
            id="launch-buy-price"
            type="range"
            min={minLocal}
            max={maxLocal}
            step={step}
            value={priceLocal}
            onChange={e => setPriceEur(Number(e.target.value) / cur.perEur)}
            className="w-full accent-[var(--launch-accent-2)]"
          />
        </div>
        {/* Lines in the slide's text colour (the theme background on this inverted slide). */}
        <dl className="bb-reveal divide-y divide-background/15 border-y border-background/15 text-lg sm:text-2xl" style={{ ["--d" as string]: "380ms" }}>
          {[
            [t("buyFanPays"), money(calc.grossCents)],
            [t("buyBbFee"), `− ${money(calc.platformCents)}`],
            [t("buyCardFee", { fixed: cur.money(CARD_FIXED_EUR * cur.perEur) }), `− ${money(calc.processingCents)}`],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 py-2"><dt className="opacity-75">{label}</dt><dd className="tabular-nums">{value}</dd></div>
          ))}
          <div className="flex justify-between gap-4 py-2 font-bold"><dt>{t("buyMakerGets")}</dt><dd className="tabular-nums text-[var(--launch-accent-2)]">{money(calc.netCents)}</dd></div>
        </dl>
      </div>

      <div className="bb-reveal space-y-4" style={{ ["--d" as string]: "300ms" }}>
        <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.2em] opacity-75">{t("buyToEarn", { amount: money(calc.netCents) })}</p>
        <ul className="space-y-1.5" aria-live="polite">
          {/* Phones: the platform's logo (name kept for screen readers), the
              bar, and the count in its own column so nothing is cut off
              (Gordon 2026-11-01: "layout don't fit mobile screen … just the
              streaming company logos in mobile"). */}
          {rows.map(r => (
            <li key={r.key}>
              <button
                type="button"
                disabled={r.needed === null}
                aria-pressed={r.key === pickedRow.key}
                onClick={() => setPicked(r.key)}
                className={cn(
                  "grid w-full grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-2 py-1 text-left transition-colors sm:grid-cols-[11.5rem_minmax(0,1fr)_auto] sm:gap-3",
                  "enabled:cursor-pointer enabled:hover:bg-[color-mix(in_srgb,currentColor_8%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-current",
                  r.key === pickedRow.key && "bg-[color-mix(in_srgb,currentColor_12%,transparent)] ring-2 ring-current",
                )}
              >
              <span className={cn("flex min-w-0 items-center gap-2 text-sm sm:text-base font-semibold", r.key === pickedRow.key && "text-[var(--launch-accent-2)]")} title={r.label}>
                {hasPlatformLogo(r.key) && <PlatformLogo id={r.key} decorative className="h-6 w-6 shrink-0" />}
                <span className={cn("truncate", hasPlatformLogo(r.key) && "max-sm:sr-only")}>{r.label}</span>
                {r.estimate && <span aria-hidden className="max-sm:-ml-1.5 max-sm:self-start max-sm:text-xs">*</span>}
              </span>
              {r.needed === null ? (
                <span className="col-span-2 text-sm opacity-70">{t("buyNoRate")}</span>
              ) : (
                <>
                  <span className="flex min-w-0 items-center">
                    <span
                      className={cn("h-5 sm:h-6 shrink-0 rounded-full transition-[width] duration-500", r.key === pickedRow.key ? "bg-[var(--launch-accent-2)]" : "bg-[var(--launch-bg)]")}
                      style={{ width: `${barWidth(r.needed)}%` }}
                      aria-hidden
                    />
                  </span>
                  <span className="whitespace-nowrap text-right text-sm sm:text-base font-semibold tabular-nums">
                    {count(r.needed)} {unitWord(r.unit)}
                  </span>
                </>
              )}
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs sm:text-sm leading-relaxed opacity-70">
          {t("buyNote", { date: checked })} {t("buyFootnote")}{" "}
          {Object.values(RATE_SOURCES).map((s, i) => (
            <span key={s.url}>
              {i > 0 && " · "}
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-100">{s.label}</a>
            </span>
          ))}
        </p>
        {/* Which exchange rate, from where, and how fresh (Gordon 2026-11-01). */}
        <p className="text-xs sm:text-sm leading-relaxed opacity-70">
          {fxDate && cur.source
            ? <>{t("buyFxNote", { date: fxDate })}{" "}<a href={cur.source.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-100">{cur.source.label}</a></>
            : t("buyFxPending")}
        </p>
      </div>
    </div>
  );
}
